import * as React from 'react'
import { userGroupService } from '$utils/services/userGroupService'
import history from '$utils/history'
import { routeProvider } from '$utils/routeProvider'
import { PrivateHeader } from '$views/components/Layout/PrivateHeader'
import { Container } from '$views/components/Layout/Container'
import { FormContainer } from '$views/components/FormContainer'
import { LocationStep } from '$views/RecommendationsForms/ResidenceForm/LocationStep'
import { DateStep } from '$views/RecommendationsForms/ResidenceForm/DateStep'
import { ResidentStep } from '$views/RecommendationsForms/ResidenceForm/ResidentStep/ResidentStep'
import { CareStep } from '$views/RecommendationsForms/ResidenceForm/CareStep'
import { SummaryStep } from '$views/RecommendationsForms/ResidenceForm/SummaryStep'
import { tagManager } from '$infrastructure/tagManager/tagManager'
import { Background } from '$views/components/Background'
import { CaretakerCard } from '$views/components/CaretakerCard'
import { CaretakerDetailsView } from '$domain/model/caretaker/caretakerDetails/caretakerDetails.model'
import { getCaretakerDetails } from '$services/caretaker/caretakerDetails/caretakerDetails.service'
import { Spinner } from '$views/components/Spinner'
import { formatError } from '$views/components/Error/Error.mapper'
import { ErrorView } from '$views/components/Error/Error.model'
import { Error } from '$views/components/Error'

export enum ResidenceFormSteps {
  locationStep = 'locationStep',
  dateStep = 'dateStep',
  residentStep = 'residentStep',
  careStep = 'careStep',
  summaryStep = 'summaryStep',
}

export const orderedResidenceFormSteps = [
  ResidenceFormSteps.locationStep,
  ResidenceFormSteps.dateStep,
  ResidenceFormSteps.residentStep,
  ResidenceFormSteps.careStep,
  ResidenceFormSteps.summaryStep,
]

export const residenceFormDraftName = 'residenceForm'

interface Props {
  caretakerId?: string
}

export const ResidenceForm: React.FC<Props> = ({ caretakerId }) => {
  const [currentStep, setCurrentStep] = React.useState<number>(0)
  const [caretakerDetails, setCaretakerDetails] = React.useState<CaretakerDetailsView>(undefined)
  const [isLoading, handleLoading] = React.useState<boolean>(false)
  const [errorNotification, setErrorNotification] = React.useState<ErrorView>()
  const [showErrorNotification, handleShowErrorNotification] = React.useState<boolean>(false)

  const loadCaretaker = async id => {
    handleLoading(true)
    try {
      const caretaker = await getCaretakerDetails(id)
      setCaretakerDetails(caretaker)
      handleLoading(false)
    } catch (error) {
      const requestError = formatError(error)
      setErrorNotification(requestError)
      handleShowErrorNotification(true)
      handleLoading(false)
    }
  }

  React.useEffect(() => {
    if (!(userGroupService.isResidenceAdmin() || userGroupService.isResidenceModerator())) {
      history.push(routeProvider.recommendationStepForm)
    }
    if (caretakerId) {
      loadCaretaker(caretakerId)
    }

    tagManager.push({
      event: 'startform',
      serviceType: 'Corto',
      offerType: 'recomendación',
    })
  }, [])

  const onNextStep = () => setCurrentStep(step => step + 1)

  const onPreviousStep = () => setCurrentStep(step => step - 1)

  const onSelectedStep = step => {
    const slectedStep = orderedResidenceFormSteps.findIndex(orderedSteps => orderedSteps === step)
    setCurrentStep(slectedStep)
  }

  const showCaretaker = () => Boolean(caretakerDetails)

  return (
    <>
      {isLoading && <Spinner />}
      {showErrorNotification && <Error error={errorNotification} onClick={() => handleShowErrorNotification(false)} />}
      <PrivateHeader />
      {showCaretaker() && (
        <Background color="xlightGrey">
          <CaretakerCard caretakerDetails={caretakerDetails} />
        </Background>
      )}
      <Background color="white" noTop={!showCaretaker()}>
        <Container noTop={showCaretaker()}>
          <FormContainer login noTop={showCaretaker()}>
            {ResidenceFormSteps.locationStep === orderedResidenceFormSteps[currentStep] && (
              <LocationStep onNextStep={onNextStep} />
            )}
            {ResidenceFormSteps.dateStep === orderedResidenceFormSteps[currentStep] && (
              <DateStep onNextStep={onNextStep} onPreviousStep={onPreviousStep} />
            )}
            {ResidenceFormSteps.residentStep === orderedResidenceFormSteps[currentStep] && (
              <ResidentStep onNextStep={onNextStep} onPreviousStep={onPreviousStep} />
            )}
            {ResidenceFormSteps.careStep === orderedResidenceFormSteps[currentStep] && (
              <CareStep onNextStep={onNextStep} onPreviousStep={onPreviousStep} />
            )}
            {ResidenceFormSteps.summaryStep === orderedResidenceFormSteps[currentStep] && (
              <SummaryStep onSelectedStep={onSelectedStep} onPreviousStep={onPreviousStep} caretakerId={caretakerId} />
            )}
          </FormContainer>
        </Container>
      </Background>
    </>
  )
}



// WEBPACK FOOTER //
// ./src/views/RecommendationsForms/ResidenceForm/ResidenceForm.tsx